import { createSlice } from "@reduxjs/toolkit";
import toast from "utils/ToastNotistack";
import { AxiosError } from "axios";
import { Dispatch } from "redux";
import authApi from "interceptor/authInterceptor";

export type DeveloperAccountSettingsProps = {
  loader: boolean;
  error: string | null;
  action: string;
  apiKey: string;
  secret: string;
  accessToken: string;
  refreshToken: string;
};

const initialState: DeveloperAccountSettingsProps = {
  loader: false,
  error: null,
  action: "",
  apiKey: "",
  secret: "",
  accessToken: "",
  refreshToken: "",
};

const DeveloperAccountSetting = createSlice({
  name: "developerAccountSetting",
  initialState,
  reducers: {
    isLoading(state, action) {
      state.loader = action.payload;
    },
    hasError(state, action) {
      state.error = action.payload;
    },
    setApiKeys(state, action) {
      state.apiKey = action.payload.x_api_key;
      state.secret = action.payload.secret_key;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.action = "setApiKeysSuccess";
    },
    clearApiKeys(state) {
      state.apiKey = "";
      state.secret = "";
      state.accessToken = "";
      state.refreshToken = "";
      state.action = "";
    },
    clearDeveloperAccountSettings: () => initialState,
  },
});

export default DeveloperAccountSetting.reducer;

export const {
  isLoading,
  hasError,
  setApiKeys,
  clearApiKeys,
  clearDeveloperAccountSettings,
} = DeveloperAccountSetting.actions;

export const fetchApiKeys = async (dispatch: Dispatch, companyId: string) => {
  try {
    if (!companyId) {
      throw new Error("Company ID is missing");
    }
    dispatch(isLoading(true));

    const response = await authApi.get(
      `v2/api/developerAccount/company/${companyId}/generate-api-key-secret`
    );
    const data = response.data;
    if (data && data.success) {
      const { apiKey, secret, accessToken, refreshToken } = data.data;
      const payload = {
        x_api_key: apiKey || "",
        secret_key: secret || "",
        accessToken: accessToken || "",
        refreshToken: refreshToken || "",
      };
      dispatch(setApiKeys(payload));
      dispatch(hasError(null));
    } else {
      dispatch(clearApiKeys());
      dispatch(hasError(data.message || "Failed to fetch API keys"));
      toast(data.message || "Failed to fetch API keys", { variant: "error" });
    }
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    if (axiosError.response?.status === 404) {
      dispatch(clearApiKeys());
      dispatch(hasError(null));
      toast("No API keys found for this company", { variant: "info" });
    } else {
      const errorMessage =
        axiosError.response?.data?.message ||
        axiosError.message ||
        "Failed to fetch API keys";
      dispatch(clearApiKeys());
      toast(errorMessage, { variant: "error" });
    }
  } finally {
    dispatch(isLoading(false));
  }
};

export const createApiAndSecretKey = async (
  dispatch: Dispatch,
  companyId: string
) => {
  try {
    if (!companyId) {
      throw new Error("Company ID is missing");
    }
    dispatch(isLoading(true));
    const response = await authApi.post(
      `v2/api/developerAccount/company/${companyId}/generate-api-key-secret`,
      {}
    );
    const data = response.data;

    if (data && data.success) {
      const payload = {
        x_api_key: data.data.apiKey || "",
        secret_key: data.data.secret || "",
        accessToken: data.data.accessToken || "",
        refreshToken: data.data.refreshToken || "",
      }
      dispatch(setApiKeys(payload));
      dispatch(hasError(null));
      toast("API key and secret generated successfully.", { variant: "success" });
    } else {
      dispatch(hasError(data.message || "Failed to generate API keys"));
      toast(data.message || "Failed to generate API keys", {
        variant: "error",
      });
    }
  } catch (error) {
    const errorMessage =
      (error as AxiosError<{ message?: string }>).response?.data?.message ||
      (error as AxiosError).message ||
      "Failed to generate API keys";
    dispatch(hasError(errorMessage));
    toast(errorMessage, { variant: "error" });
  } finally {
    dispatch(isLoading(false));
  }
};

export function getApiAndSecretKeys(companyId: string) {
  return async (dispatch: Dispatch) => {
    await fetchApiKeys(dispatch, companyId);
  };
}

export function generateApiAndSecretKeys(companyId: string) {
  return async (dispatch: Dispatch) => {
    await createApiAndSecretKey(dispatch, companyId);
  };
}
